import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  DialogActions,
  DialogContentText,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { navigateAfterImpersonation } from '../../services/auth/postImpersonationRedirect';
import type { BranchOption, CompanyUser } from '../../services/SuperAdminService';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import LoginIcon from '@mui/icons-material/Login';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SuperAdminService from '../../services/SuperAdminService';
import { isUsageProductModule, usageModuleLabel } from './usageModuleLabels';

interface CompanyDetailsModalProps {
  open: boolean;
  onClose: () => void;
  company: {
    id: string;
    name: string;
    logo?: string;
    admin: {
      name: string;
      email: string;
      phone?: string;
    };
    maxPersonnel?: number;
    createdAt?: string;
  } | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const TAB_BRANCHES = 0;
const TAB_USERS = 1;
const TAB_ACTIVITY = 2;

interface CompanyTrendPoint {
  date: string; // yyyy-MM-dd
  total: number;
  uniqueUsers: number;
  web: number;
  mobile: number;
  other: number;
  label?: string;
}

interface CompanyModuleUsage {
  module: string;
  requests: number;
  uniqueUsers: number;
}

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
  open,
  onClose,
  company,
}) => {
  const [currentTab, setCurrentTab] = useState(TAB_BRANCHES);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<BranchOption[] | null>(null);
  // Kullanıcılar sekmesi (impersonation seçim listesi)
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[] | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [impersonatingId, setImpersonatingId] = useState<number | null>(null);
  const [confirmUser, setConfirmUser] = useState<CompanyUser | null>(null);
  const [impError, setImpError] = useState<string | null>(null);
  // Giriş geçmişi sekmesi
  const [activityTrend, setActivityTrend] = useState<CompanyTrendPoint[] | null>(null);
  const [moduleUsage, setModuleUsage] = useState<CompanyModuleUsage[] | null>(null);

  const navigate = useNavigate();
  const { impersonate } = useAuth();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    // Sekme değiştiğinde ilgili veriyi yükle
    loadTabData(newValue);
  };

  // Sekme verilerini yükle
  const loadTabData = async (tabIndex: number) => {
    if (!company) return;

    if (tabIndex === TAB_BRANCHES) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const data = await superAdminService.getCompanyBranchOptions(company.id);
        setBranches(data);
      } catch (error) {
        console.error('[CompanyDetailsModal] Şube verileri yüklenirken hata:', error);
        setBranches(null);
      } finally {
        setLoading(false);
      }
    } else if (tabIndex === TAB_USERS) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const users = await superAdminService.getCompanyUsers(company.id);
        // Şirket yöneticisi en üstte dursun; geri kalanın sırası backend'den
        // geldiği gibi korunur (JS sort kararlıdır).
        setCompanyUsers(
          [...users].sort(
            (a, b) => Number((b.role || '').toLowerCase() === 'admin') - Number((a.role || '').toLowerCase() === 'admin')
          )
        );
      } catch (error) {
        console.error('Kullanıcılar yüklenirken hata:', error);
        setCompanyUsers([]);
      } finally {
        setLoading(false);
      }
    } else if (tabIndex === TAB_ACTIVITY) {
      setLoading(true);
      try {
        const superAdminService = SuperAdminService.getInstance();
        const [trendRes, modulesRes] = await Promise.all([
          superAdminService.getCompanyLoginTrend(company.id, 30),
          superAdminService.getCompanyUsageModules(company.id, 30).catch(() => null),
        ]);
        const data: CompanyTrendPoint[] =
          trendRes?.success && Array.isArray(trendRes.data)
            ? (trendRes.data as CompanyTrendPoint[]).map((p) => {
                const d = new Date(`${p.date}T00:00:00`);
                const label = Number.isNaN(d.getTime())
                  ? p.date
                  : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
                return { ...p, label };
              })
            : [];
        setActivityTrend(data);
        setModuleUsage(
          modulesRes?.success && Array.isArray(modulesRes.modules)
            ? (modulesRes.modules as CompanyModuleUsage[]).filter((m) => isUsageProductModule(m.module))
            : []
        );
      } catch (error) {
        console.error('[CompanyDetailsModal] Giriş geçmişi yüklenirken hata:', error);
        setActivityTrend([]);
        setModuleUsage([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // Onay modalını açar (ikon tıklaması).
  const requestImpersonate = (u: CompanyUser) => {
    setConfirmUser(u);
  };

  // Onaylanan kullanıcıya geç: token al → modalları kapat → role göre yönlendir.
  const confirmImpersonate = async () => {
    if (!company || !confirmUser) return;
    const u = confirmUser;
    setImpersonatingId(u.id);
    try {
      const authUser = await impersonate({ targetUserId: u.id });
      setConfirmUser(null);
      onClose();
      await navigateAfterImpersonation(
        { role: u.role, companyId: company.id, uid: authUser.uid },
        (path) => navigate(path)
      );
    } catch (e: any) {
      console.error('[CompanyDetailsModal] Impersonation hatası:', e);
      setConfirmUser(null);
      setImpError(e?.message || 'Kullanıcı oturumuna geçilemedi.');
    } finally {
      setImpersonatingId(null);
    }
  };

  // Modal açıldığında veya şirket değiştiğinde veriyi temizle ve yeniden yükle
  useEffect(() => {
    if (open && company) {
      setBranches(null);
      setCompanyUsers(null);
      setActivityTrend(null);
      setModuleUsage(null);
      setUserSearch('');
      setCurrentTab(TAB_BRANCHES);
      loadTabData(TAB_BRANCHES);
    } else if (!open) {
      setBranches(null);
      setCompanyUsers(null);
      setActivityTrend(null);
      setModuleUsage(null);
      setUserSearch('');
      setCurrentTab(TAB_BRANCHES);
    }
  }, [open, company?.id]);

  if (!company) return null;

  // Tarih formatı
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      return '-';
    }
  };

  // Tab tanımlamaları
  const tabs = [
    { label: 'Şubeler', icon: <BusinessIcon />, id: 'branches' },
    { label: 'Kullanıcılar', icon: <PeopleAltIcon />, id: 'users' },
    { label: 'Giriş Geçmişi', icon: <ShowChartIcon />, id: 'activity' },
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Modal Başlığı */}
      <DialogTitle
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2.5, flex: 1 }}>
            {/* Logo ve Şirket Adı */}
            <Avatar
              src={company.logo}
              alt={company.name}
              sx={{ width: 64, height: 64, bgcolor: 'white', color: '#1976d2', fontSize: '1.5rem' }}
            >
              {company.name.charAt(0)}
            </Avatar>

            {/* Şirket Bilgileri */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {company.name}
              </Typography>

              {/* Bilgi Satırları - 3 sütun, dar gap */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 0.75,
                rowGap: 0.5
              }}>
                {/* Admin Bilgisi */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    <strong>Admin:</strong> {company.admin.name}
                  </Typography>
                </Box>

                {/* Email */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    {company.admin.email}
                  </Typography>
                </Box>

                {/* Telefon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    {company.admin.phone || 'Telefon Bilgisi Yok'}
                  </Typography>
                </Box>

                {/* Max Personel */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleAltIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    <strong>Max Personel:</strong> {company.maxPersonnel || 0}
                  </Typography>
                </Box>

                {/* Kayıt Tarihi */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.85rem' }}>
                    <strong>Kayıt:</strong> {formatDate(company.createdAt)}
                  </Typography>
                </Box>

                {/* Şirket ID */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FingerprintIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.95, 
                      fontSize: '0.8rem',
                      fontFamily: 'monospace',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.5
                    }}
                  >
                    {company.id}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Kapatma Butonu */}
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              ml: 2,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Tab Başlıkları */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: '#f5f5f5',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#1976d2',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
              height: 3,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              id={`company-tab-${index}`}
              aria-controls={`company-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab İçerikleri */}
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'hidden',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Şubeler */}
            <TabPanel value={currentTab} index={TAB_BRANCHES}>
              {branches ? (
                <Box>
                  {/* Özet Kart */}
                  <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <BusinessIcon sx={{ fontSize: 56, color: '#1976d2', mb: 1 }} />
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {branches.length}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        Toplam Şube
                      </Typography>
                      {branches.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {branches.filter((b) => b.isActive).length} aktif ·{' '}
                          {branches.filter((b) => b.isFranchise).length} franchise
                        </Typography>
                      )}
                    </CardContent>
                  </Card>

                  {branches.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Bu şirkete henüz şube eklenmemiş.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 48, fontWeight: 700 }}>#</TableCell>
                            <TableCell>Şube Adı</TableCell>
                            <TableCell>İl</TableCell>
                            <TableCell>Telefon</TableCell>
                            <TableCell>Firma Unvanı</TableCell>
                            <TableCell>VKN</TableCell>
                            <TableCell align="center">Tip</TableCell>
                            <TableCell align="center">Durum</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {branches.map((b, index) => (
                            <TableRow key={b.branchId} hover>
                              <TableCell sx={{ color: 'text.secondary', fontWeight: 700, width: 48 }}>{index + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>{b.name || '-'}</TableCell>
                              <TableCell>{b.city || '-'}</TableCell>
                              <TableCell>{b.phone || '-'}</TableCell>
                              <TableCell>{b.firmName || '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{b.firmTaxNumber || '-'}</TableCell>
                              <TableCell align="center">
                                {b.isFranchise ? (
                                  <Chip label="Franchise" size="small" color="secondary" variant="outlined" />
                                ) : (
                                  <Chip label="Şube" size="small" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={b.isActive ? 'Aktif' : 'Pasif'}
                                  size="small"
                                  color={b.isActive ? 'success' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Veri yüklenemedi
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {/* Kullanıcılar — impersonation seçim listesi */}
            <TabPanel value={currentTab} index={TAB_USERS}>
              {companyUsers ? (
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="İsim, e-posta veya role göre ara..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {(() => {
                    const q = userSearch.trim().toLowerCase();
                    const filtered = companyUsers.filter((u) => {
                      if (!q) return true;
                      const hay = `${u.firstName} ${u.lastName} ${u.email} ${u.role}`.toLowerCase();
                      return hay.includes(q);
                    });

                    if (companyUsers.length === 0) {
                      return (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Bu şirkette aktif kullanıcı bulunamadı.
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 48, fontWeight: 700 }}>#</TableCell>
                              <TableCell>Ad Soyad</TableCell>
                              <TableCell>E-posta</TableCell>
                              <TableCell>Rol</TableCell>
                              <TableCell align="right" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filtered.map((u, index) => {
                              const fullName = `${u.firstName} ${u.lastName}`.trim() || '—';
                              const busy = impersonatingId === u.id;
                              return (
                                <TableRow key={u.id} hover>
                                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700, width: 48 }}>{index + 1}</TableCell>
                                  <TableCell>{fullName}</TableCell>
                                  <TableCell>{u.email}</TableCell>
                                  <TableCell>
                                    <Chip label={u.role || '-'} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell align="right">
                                    {/* Nötr, dikkat çekmeyen geçiş ikonu */}
                                    <Tooltip title="Bu kullanıcının oturumuna geç">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={impersonatingId !== null}
                                          onClick={() => requestImpersonate(u)}
                                          sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                                        >
                                          {busy ? <CircularProgress size={16} /> : <LoginIcon fontSize="small" />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {filtered.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Aramayla eşleşen kullanıcı yok.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Veri yüklenemedi
                  </Typography>
                </Box>
              )}
            </TabPanel>

            {/* Giriş Geçmişi — son 30 günün günlük giriş grafiği */}
            <TabPanel value={currentTab} index={TAB_ACTIVITY}>
              {activityTrend ? (
                (() => {
                  const total = activityTrend.reduce((s, p) => s + p.total, 0);
                  const peak = activityTrend.reduce((m, p) => (p.total > m ? p.total : m), 0);
                  const activeDays = activityTrend.filter((p) => p.total > 0).length;
                  if (total === 0) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <ShowChartIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          Son 30 günde bu şirkette giriş kaydı yok.
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>{total}</Typography>
                            <Typography variant="caption" color="text.secondary">Toplam giriş (30 gün)</Typography>
                          </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{activeDays}</Typography>
                            <Typography variant="caption" color="text.secondary">Aktif gün sayısı</Typography>
                          </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ flex: '1 1 140px' }}>
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>{peak}</Typography>
                            <Typography variant="caption" color="text.secondary">En yoğun gün</Typography>
                          </CardContent>
                        </Card>
                      </Box>
                      <Box sx={{ width: '100%', height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={activityTrend} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                            <defs>
                              <linearGradient id="companyTrafficFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#1976d2" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                            <RechartsTooltip labelFormatter={(label) => `Tarih: ${label}`} />
                            <Area type="monotone" dataKey="total" name="Giriş" stroke="#1976d2" strokeWidth={2} fill="url(#companyTrafficFill)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Her başarılı giriş bir olay olarak sayılır. Gün kovaları UTC tarihine göredir.
                      </Typography>

                      {/* Modül kullanımı — bu şirket neyi kullanıyor? (istek bazlı, son 30 gün) */}
                      {moduleUsage && moduleUsage.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Modül Kullanımı (İstek Bazlı, Son 30 Gün)
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            İstek = o işe atılan API çağrısı. E-ticaret, panel + vitrin sitesini kapsar.
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: 48, fontWeight: 700 }}>#</TableCell>
                                  <TableCell>Modül</TableCell>
                                  <TableCell align="right">İstek</TableCell>
                                  <TableCell align="right">Kullanan Kişi</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {moduleUsage.map((m, index) => (
                                  <TableRow key={m.module} hover>
                                    <TableCell sx={{ color: 'text.secondary', fontWeight: 700, width: 48 }}>{index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}>{usageModuleLabel(m.module)}</TableCell>
                                    <TableCell align="right">{m.requests}</TableCell>
                                    <TableCell align="right">{m.uniqueUsers}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  );
                })()
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Veri yüklenemedi
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Impersonation onay modalı (alert/confirm yerine) */}
    <Dialog
      open={!!confirmUser}
      onClose={() => impersonatingId === null && setConfirmUser(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LoginIcon color="primary" fontSize="small" />
        Kullanıcı oturumuna geç
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          {confirmUser && (
            <>
              <Box sx={{ mb: 1 }}>
                Aşağıdaki kullanıcının oturumuna geçmek üzeresiniz:
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {`${confirmUser.firstName} ${confirmUser.lastName}`.trim() || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {confirmUser.email}
                </Typography>
                <Chip label={confirmUser.role || '-'} size="small" variant="outlined" sx={{ mt: 0.5 }} />
              </Box>
              <Box sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
                Bu kullanıcının gördüğü ekranlar açılacaktır. İstediğiniz an sağ alttaki
                butonla yönetici hesabınıza dönebilirsiniz.
              </Box>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setConfirmUser(null)}
          disabled={impersonatingId !== null}
          color="inherit"
        >
          İptal
        </Button>
        <Button
          onClick={confirmImpersonate}
          variant="contained"
          disabled={impersonatingId !== null}
          startIcon={impersonatingId !== null ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
        >
          Geç
        </Button>
      </DialogActions>
    </Dialog>

    {/* Hata bildirimi (alert yerine) */}
    <Snackbar
      open={!!impError}
      autoHideDuration={6000}
      onClose={() => setImpError(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" variant="filled" onClose={() => setImpError(null)} sx={{ width: '100%' }}>
        {impError}
      </Alert>
    </Snackbar>
    </>
  );
};

export default CompanyDetailsModal;
